import { useAuthContext } from "providers/Auth";
import { Links } from "./constants";

export const shouldShowGlobalLink = (
  name: string,
  type: ReturnType<typeof useAuthContext>["type"]
) => {
  // Native chat lives in the Inbox and ships to BoomScreen-only partners too, so
  // surface the Inbox for them even without the BoomCRM communication features —
  // but only while chat is actually switched on, or they land on an Inbox with
  // no composer tab at all. Boom ops also get it before any flag is on, to
  // verify the chat migration.
  if (name === Links.INBOX) {
    // The Inbox is meaningless for a rent-reporting-only partner (no BoomCRM and
    // no BoomScreen), so hide it for everyone — Boom ops included.
    if (!type.isBoomCRM && !type.isBoomScreen) return false;
    // The free Core plan grants communication + communication_sms, so a free
    // partner reaches the SMS-only Inbox through the isBoomCRM arm (CRM-759).
    return (
      (type.isBoomCRM && (type.isCommunication || type.isCommunicationSms)) ||
      (type.isNativeChatActive && (type.isBoomScreen || type.isBoomCRM)) ||
      type.isBoomOps
    );
  }
  if (name === Links.CONTACTS) return type.isBoomCRM;
  // Tasks is Pro-only, but the menu item stays for the whole CRM ecosystem: a
  // free partner clicking it gets the upgrade prompt instead of nothing at all
  // (CRM-759). The page itself paywalls when the entitlement is missing.
  if (name === Links.TASKS) return type.isBoomCRM;
  // Analytics is always reachable; the crm_analytics flag only gates the
  // BoomCRM subtab inside the view.
  // Provisioned numbers come with the free Core plan, so the item stays for the
  // whole CRM ecosystem and the page paywalls itself when the entitlement is
  // revoked — hiding it left /phone-numbers on a bare "Not found" (CRM-759).
  if (name === Links.PHONE_NUMBERS) return type.isBoomCRM;
  // Workflows are granted by the free Core plan (capped at one). When an admin
  // revokes the entitlement the item stays, like Tasks / Phone numbers above,
  // and the page paywalls itself (CRM-759 QA). The BoomScreen requirement
  // stays: workflows trigger on applications.
  if (name === Links.WORKFLOWS) return type.isBoomCRM && type.isBoomScreen;

  return !(
    (type.isPropertyManager || !type.isBoomScreen) &&
    name === Links.PROPERTY_GROUPS
  );
};

export const shouldShowReportingLinks = (
  name: string,
  type: ReturnType<typeof useAuthContext>["type"],
  partner: ReturnType<typeof useAuthContext>["partner"],
  pms: ReturnType<typeof useAuthContext>["pms"]
) => {
  const isAppfolioWithoutSupergood =
    pms.isAppfolio && !partner?.appfolio_supergood_enabled;

  const isBlockedRenter =
    name === Links.RENTERS &&
    (!type.isPMS ||
      (type.isBoomScreen && !type.isRentReporting) ||
      isAppfolioWithoutSupergood);

  return !isBlockedRenter;
};
